import './echarts-register';
import './style.css';
import './views/pop/pop-touch.css';
import 'vue-echarts/style.css';
import {
  appendAdminChildRoutes,
  AX_TIER_DEFAULT_COLOR_PRESET,
  coreRoutes,
  createAxApp,
  createAxResourceRoute,
  replaceAdminDashboardRoute,
  SystemDocumentTemplatesPage,
  type AxAppContext,
  type AxResourceRouteDefinition,
  type BrandingConfig,
} from '@axsolutionsdev/core-fe';
import { AgentDockButton } from '@axsolutionsdev/core-fe';
import MesAgentPanel from './components/agent/MesAgentPanel.vue';
import type { Component } from 'vue';
import type { RouteRecordRaw } from 'vue-router';
import { mergeMesBranding } from './branding';
import {
  PROCESS_DATA_STATUS_ROUTE_DEFS,
  resolveProcessDataStatusProfile,
} from './views/data-status/process-data-status.config.js';
import type { ProcessDataStatusProfileId } from './views/data-status/process-data-status.types.js';

/**
 * `crypto.randomUUID` 는 secure context(HTTPS·localhost)에서만 제공된다. dev 를 원격
 * IP(http://ip:port)로 열면 undefined 라 로컬 임시 id 생성(`crypto.randomUUID()`)이 TypeError 로
 * 터진다(예: 단위 관리 「행 추가」, BOM·수주·입출고 라인 추가). `getRandomValues` 기반 v4 UUID 로
 * 폴백한다. 운영은 HTTPS 라 네이티브 구현이 그대로 쓰이며, 이 폴백은 dev-원격 접속에서만 동작한다.
 */
function installRandomUuidPolyfill(): void {
  const c = globalThis.crypto as Crypto | undefined;
  if (!c || typeof c.getRandomValues !== 'function' || typeof c.randomUUID === 'function') return;
  const gen = (): string => {
    const b = new Uint8Array(16);
    c.getRandomValues(b);
    b[6] = ((b[6] ?? 0) & 0x0f) | 0x40; // version 4
    b[8] = ((b[8] ?? 0) & 0x3f) | 0x80; // variant 10xx
    const hex = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  };
  try {
    (c as { randomUUID: Crypto['randomUUID'] }).randomUUID = gen as Crypto['randomUUID'];
  } catch {
    (Crypto.prototype as { randomUUID: Crypto['randomUUID'] }).randomUUID =
      gen as Crypto['randomUUID'];
  }
}
installRandomUuidPolyfill();

// (L.MES.SK.4b) 아래 3종은 Tier 3 확장용으로 re-export 되므로 boundary 타입을 명시한다.
type AsyncPage = () => Promise<{ default: Component }>;
const MesResourcePage: AsyncPage = () => import('./views/MesResourcePage.vue');
const MesOperationalResourcePage: AsyncPage = () => import('./views/MesOperationalResourcePage.vue');
const MesStatusPage: AsyncPage = () => import('./views/MesStatusPage.vue');
const QualityDashboardPage = () => import('./views/quality/QualityDashboardPage.vue');

const ItemsPage = () => import('./views/master/ItemsPage.vue');
const ProductionPlansPage = () => import('./views/production/ProductionPlansPage.vue');
const ProductionRecordsPage = () => import('./views/production/ProductionRecordsPage.vue');
const WorkOrdersPage = () => import('./views/production/WorkOrdersPage.vue');
const PurchaseOrdersPage = () => import('./views/sales/PurchaseOrdersPage.vue');
const SalesOrdersPage = () => import('./views/sales/SalesOrdersPage.vue');
const WarehousesPage = () => import('./views/master/WarehousesPage.vue');
const PartnersPage = () => import('./views/master/PartnersPage.vue');
const ProcessesPage = () => import('./views/master/ProcessesPage.vue');
const EquipmentsPage = () => import('./views/master/EquipmentsPage.vue');
const EquipmentHistoryPage = () => import('./views/master/EquipmentHistoryPage.vue');
const EquipmentSensorsPage = () => import('./views/master/EquipmentSensorsPage.vue');
const BomsPage = () => import('./views/master/BomsPage.vue');
const RoutingsPage = () => import('./views/master/RoutingsPage.vue');
// 업무문서 인쇄는 코어 문서 서식 엔진의 범용 페이지 1개로 수렴한다 (L.MES.DOC.1).
const DocumentPrintPage = () => import('./reports/DocumentPrintPage.vue');
const MesPopEntryPage = () => import('./views/pop/MesPopEntryPage.vue');
const MesPopStockIoHubPage = () => import('./views/pop/MesPopStockIoHubPage.vue');
const MesPopDataMonitorPage = () => import('./views/pop/MesPopDataMonitorPage.vue');
const MesDataStatusPlaceholderPage = () => import('./views/MesDataStatusPlaceholderPage.vue');
const MesProcessDataStatusPage = () =>
  import('./views/data-status/JayeonBadaProcessDataStatusPage.vue');
const MesDashboardPage = () => import('./views/MesDashboardPage.vue');
const GoodsReceiptsPage = () => import('./views/inventory/GoodsReceiptsPage.vue');
const GoodsIssuesPage = () => import('./views/inventory/GoodsIssuesPage.vue');
const MaterialIssuesPage = () => import('./views/inventory/MaterialIssuesPage.vue');
const InventoryTransfersPage = () => import('./views/inventory/InventoryTransfersPage.vue');
const InventoryCountsPage = () => import('./views/inventory/InventoryCountsPage.vue');
const InventoryStockStatusPage = () => import('./views/inventory/InventoryStockStatusPage.vue');

// sHACCP 화면 (L.MES.HACCP.1)
const HaccpDashboardPage = () => import('./views/haccp/HaccpDashboardPage.vue');
const HygieneCheckLogDailyPage = () => import('./views/haccp/HygieneCheckLogPage.vue');
const HygieneCheckLogVisualPage = () => import('./views/haccp/HygieneCheckLogPage.vue');
const HaccpProductionLogPage = () => import('./views/haccp/HaccpProductionLogPage.vue');
const HaccpMaterialLedgerPage = () => import('./views/haccp/HaccpMaterialLedgerPage.vue');
const HaccpProductLedgerPage = () => import('./views/haccp/HaccpProductLedgerPage.vue');
const HygieneCheckFormPage = () => import('./views/haccp/HygieneCheckFormPage.vue');

// HACCP 일지 엔진 (L.MES.HACCP.2 — docs/designs/mes-haccp-ccp-suite.md)
const CcpMasterPage = () => import('./views/haccp/CcpMasterPage.vue');
const CcpMonitoringPage = () => import('./views/haccp/CcpMonitoringPage.vue');
const HaccpTemplateDesignerPage = () => import('./views/haccp/HaccpTemplateDesignerPage.vue');
const HaccpSheetListPage = () => import('./views/haccp/HaccpSheetListPage.vue');
const HaccpSheetPage = () => import('./views/haccp/HaccpSheetPage.vue');
const HaccpSheetPrintPage = () => import('./reports/HaccpSheetPrintPage.vue');
const HealthCertPage = () => import('./views/haccp/HealthCertPage.vue');

// SPC (통계적 공정관리)
const SpcManagementPage = () => import('./views/quality/SpcManagementPage.vue');

type MesResourceRoute = AxResourceRouteDefinition;

/** A4 인쇄·PDF 전용 라우트 (AdminLayout·메뉴 없음) */
const mesReportPrintRoutes: RouteRecordRaw[] = [
  {
    // 코어 문서 서식 엔진 — 발주서·작업지시서·거래명세서 공용 (docType 파라미터)
    path: '/documents/:docType/:sourceId/print',
    name: 'documents.print',
    component: DocumentPrintPage,
    meta: {
      requiresAuth: true,
      permission: 'system:document:render',
    },
  },
  // 구 경로 — 북마크·열린 탭 보호용 리다이렉트 (한 라운드 유지 후 제거)
  {
    path: '/reports/purchase-order/:id/print',
    name: 'reports.purchase-order.print',
    redirect: (to) => ({
      name: 'documents.print',
      params: { docType: 'PURCHASE_ORDER', sourceId: String(to.params.id ?? '') },
      query: to.query,
    }),
  },
  {
    path: '/reports/work-order/:id/print',
    name: 'reports.work-order.print',
    redirect: (to) => ({
      name: 'documents.print',
      params: { docType: 'WORK_ORDER', sourceId: String(to.params.id ?? '') },
      query: to.query,
    }),
  },
  {
    path: '/reports/haccp-sheet/:id/print',
    name: 'reports.haccp-sheet.print',
    component: HaccpSheetPrintPage,
    meta: {
      requiresAuth: true,
      permission: 'mes:haccp-sheet:read',
    },
  },
];

const mesPopRoutes: RouteRecordRaw[] = [
  {
    path: '/pop/production-records',
    name: 'pop.production-records',
    component: MesPopEntryPage,
    props: { mode: 'production-records' },
    meta: {
      requiresAuth: true,
      permission: 'mes:pop:production-record:create',
    },
  },
  {
    path: '/pop/material-issues',
    name: 'pop.material-issues',
    component: MesPopEntryPage,
    props: { mode: 'material-issues' },
    meta: {
      requiresAuth: true,
      permission: 'mes:pop:material-issue:create',
    },
  },
  {
    path: '/pop/goods-issues',
    name: 'pop.goods-issues',
    component: MesPopEntryPage,
    props: { mode: 'goods-issues' },
    meta: {
      requiresAuth: true,
      permission: 'mes:pop:goods-issue:create',
    },
  },
  {
    path: '/pop/goods-receipts',
    name: 'pop.goods-receipts',
    component: MesPopEntryPage,
    props: { mode: 'goods-receipts' },
    meta: {
      requiresAuth: true,
      permission: 'mes:pop:goods-receipt:create',
    },
  },
  {
    path: '/pop/inventory-counts',
    name: 'pop.inventory-counts',
    component: MesPopEntryPage,
    props: { mode: 'inventory-counts' },
    meta: {
      requiresAuth: true,
      permission: 'mes:pop:inventory-count:create',
    },
  },
  {
    path: '/pop/inventory-transfers',
    name: 'pop.inventory-transfers',
    component: MesPopEntryPage,
    props: { mode: 'inventory-transfers' },
    meta: {
      requiresAuth: true,
      permission: 'mes:pop:inventory-transfer:create',
    },
  },
  {
    path: '/pop/stock-io-hub',
    name: 'pop.stock-io-hub',
    component: MesPopStockIoHubPage,
    meta: {
      requiresAuth: true,
      menuCode: 'mes.pop.stock-io-hub',
      permission: 'mes:pop-hub:read',
    },
  },
  {
    path: '/pop/data-monitor',
    name: 'pop.data-monitor',
    component: MesPopDataMonitorPage,
    meta: {
      requiresAuth: true,
      menuCode: 'mes.pop.data-monitor',
      permission: 'mes:data-monitor:read',
    },
  },
];

/**
 * Tier 2 MES 라우트를 AdminLayout 에 한 번만 등록한다 (HMR 중복 방지).
 *
 * Tier 3/4 FE(`@axsolutionsdev/mes-egm-fe` 등)는 본 모듈의 `createMesFeApp()`을 호출해
 * 동일 화면을 계승한다 — 소스 복제 없이 `@axsolutionsdev/mes-ax-fe/bootstrap` 으로 공유.
 */
let mesFeRoutesInstalled = false;

function buildMesDataStatusRoutes(profileId: ProcessDataStatusProfileId): RouteRecordRaw[] {
  const profile = resolveProcessDataStatusProfile(profileId);
  const processRoutes: RouteRecordRaw[] = PROCESS_DATA_STATUS_ROUTE_DEFS.map((def) => {
    const binding = profile[def.routeKey];
    return {
      path: `mes/data-status/${def.path}`,
      name: def.name,
      component: MesProcessDataStatusPage,
      props: {
        processTypeCode: binding.processTypeCode,
        screen: binding.screen,
      },
      meta: {
        requiresAuth: true,
        menuCode: def.menuCode,
        permission: 'mes:data-status:read',
      },
    };
  });

  return [
    ...processRoutes,
    // Tier 2/3 일반 샘플 — 자연바다식품(Tier 4) 4개 공정 메뉴와 달리 시드 카탈로그에서 공통 노출.
    {
      path: 'mes/data-status/sample-process',
      name: 'mes.data-status.sample-process',
      component: MesDataStatusPlaceholderPage,
      props: {
        title: '공정현황(샘플)',
        hint: '공정 데이터(ProcessDataLog) 연동 후 이 화면에 표시됩니다.',
        permission: 'mes:data-status:read',
      },
      meta: {
        requiresAuth: true,
        menuCode: 'mes.data-status.sample-process',
        permission: 'mes:data-status:read',
      },
    },
    {
      path: 'mes/data-status/sample-equipment',
      name: 'mes.data-status.sample-equipment',
      component: MesDataStatusPlaceholderPage,
      props: {
        title: '설비현황(샘플)',
        hint: '설비 가동·센서 이력 연동 후 이 화면에 표시됩니다.',
        permission: 'mes:data-status:read',
      },
      meta: {
        requiresAuth: true,
        menuCode: 'mes.data-status.sample-equipment',
        permission: 'mes:data-status:read',
      },
    },
  ];
}

function ensureMesFeRoutesInstalled(profileId: ProcessDataStatusProfileId = 'jayeon-bada'): void {
  if (mesFeRoutesInstalled) {
    return;
  }
  mesFeRoutesInstalled = true;

  const masterRoutes: RouteRecordRaw[] = [
    {
      path: 'mes/master/items',
      name: 'mes.master.items',
      component: ItemsPage,
      meta: { requiresAuth: true, menuCode: 'mes.master.items', permission: 'mes:item:read' },
    },
    {
      path: 'mes/master/boms',
      name: 'mes.master.boms',
      component: BomsPage,
      meta: { requiresAuth: true, menuCode: 'mes.master.boms', permission: 'mes:bom:read' },
    },
    {
      path: 'mes/master/warehouses',
      name: 'mes.master.warehouses',
      component: WarehousesPage,
      meta: {
        requiresAuth: true,
        menuCode: 'mes.master.warehouses',
        permission: 'mes:warehouse:read',
      },
    },
    {
      path: 'mes/master/partners',
      name: 'mes.master.partners',
      component: PartnersPage,
      meta: { requiresAuth: true, menuCode: 'mes.master.partners', permission: 'mes:partner:read' },
    },
    {
      path: 'mes/master/processes',
      name: 'mes.master.processes',
      component: ProcessesPage,
      meta: {
        requiresAuth: true,
        menuCode: 'mes.master.processes',
        permission: 'mes:process:read',
      },
    },
    {
      path: 'mes/master/routings',
      name: 'mes.master.routings',
      component: RoutingsPage,
      meta: {
        requiresAuth: true,
        menuCode: 'mes.master.routings',
        permission: 'mes:process-routing:read',
      },
    },
    {
      path: 'mes/master/equipments',
      name: 'mes.master.equipments',
      component: EquipmentsPage,
      meta: {
        requiresAuth: true,
        menuCode: 'mes.master.equipments',
        permission: 'mes:equipment:read',
      },
    },
    {
      path: 'mes/master/equipment-history',
      name: 'mes.master.equipment-history',
      component: EquipmentHistoryPage,
      meta: {
        requiresAuth: true,
        menuCode: 'mes.master.equipment-history',
        permission: 'mes:equipment-maintenance:read',
      },
    },
    {
      path: 'mes/master/equipment-sensors',
      name: 'mes.master.equipment-sensors',
      component: EquipmentSensorsPage,
      meta: {
        requiresAuth: true,
        menuCode: 'mes.master.equipment-sensors',
        permission: 'mes:equipment-sensor:read',
      },
    },
    // 문서 서식 관리 — core 디자이너 화면 재사용, MES 메뉴 하이라이트용 alias 라우트.
    // 설계: docs/designs/mes-document-template-management.md §2 D2
    {
      path: 'mes/master/document-templates',
      name: 'mes.master.document-templates',
      component: SystemDocumentTemplatesPage,
      meta: {
        requiresAuth: true,
        menuCode: 'mes.master.document-templates',
        permission: 'system:document-template:read',
      },
    },
  ];

  const salesRoutes: (MesResourceRoute | RouteRecordRaw)[] = [
    {
      path: 'mes/sales/purchase-orders',
      name: 'mes.sales.purchase-orders',
      component: PurchaseOrdersPage,
      meta: {
        requiresAuth: true,
        menuCode: 'mes.sales.purchase-orders',
        permission: 'mes:purchase-order:read',
      },
    },
    {
      path: 'mes/sales/purchase-status',
      name: 'mes.sales.purchase-status',
      menuCode: 'mes.sales.purchase-status',
      permission: 'mes:purchase-order:read',
      title: '발주현황',
      endpoint: '/mes/purchase-orders',
    },
    {
      path: 'mes/sales/sales-orders',
      name: 'mes.sales.sales-orders',
      component: SalesOrdersPage,
      meta: {
        requiresAuth: true,
        menuCode: 'mes.sales.sales-orders',
        permission: 'mes:sales-order:read',
      },
    },
    {
      path: 'mes/sales/sales-status',
      name: 'mes.sales.sales-status',
      menuCode: 'mes.sales.sales-status',
      permission: 'mes:sales-order:read',
      title: '수주현황',
      endpoint: '/mes/sales-orders',
    },
  ];

  const productionRoutes: (MesResourceRoute | RouteRecordRaw)[] = [
    {
      path: 'mes/production/plans',
      name: 'mes.production.plans',
      component: ProductionPlansPage,
      meta: {
        requiresAuth: true,
        menuCode: 'mes.production.plans',
        permission: 'mes:production-plan:read',
      },
    },
    {
      path: 'mes/production/work-orders',
      name: 'mes.production.work-orders',
      component: WorkOrdersPage,
      meta: {
        requiresAuth: true,
        menuCode: 'mes.production.work-orders',
        permission: 'mes:work-order:read',
      },
    },
    {
      path: 'mes/production/records',
      name: 'mes.production.records',
      component: ProductionRecordsPage,
      meta: {
        requiresAuth: true,
        menuCode: 'mes.production.records',
        permission: 'mes:production-record:read',
      },
    },
    {
      path: 'mes/production/wo-record-status',
      name: 'mes.production.wo-record-status',
      menuCode: 'mes.production.wo-record-status',
      permission: 'mes:production-record:read',
      title: '작업지시별생산현황',
      endpoint: '/mes/production-records',
    },
    {
      path: 'mes/production/daily-status',
      name: 'mes.production.daily-status',
      menuCode: 'mes.production.daily-status',
      permission: 'mes:production-record:read',
      title: '일별생산현황',
      endpoint: '/mes/production-records',
    },
    {
      path: 'mes/production/record-status',
      name: 'mes.production.record-status',
      menuCode: 'mes.production.record-status',
      permission: 'mes:production-record:read',
      title: '생산실적현황',
      endpoint: '/mes/production-records',
    },
    {
      path: 'mes/production/material-issue-status',
      name: 'mes.production.material-issue-status',
      menuCode: 'mes.production.material-issue-status',
      permission: 'mes:material-issue:read',
      title: '생산자재불출현황',
      endpoint: '/mes/material-issues',
    },
    {
      path: 'mes/production/lot-tracking',
      name: 'mes.production.lot-tracking',
      menuCode: 'mes.production.lot-tracking',
      permission: 'mes:lot:read',
      title: 'Lot 추적',
      endpoint: '/mes/lots',
    },
  ];

  const qualityRoutes: MesResourceRoute[] = [
    {
      path: 'mes/quality/dashboard',
      name: 'mes.quality.dashboard',
      menuCode: 'mes.quality.dashboard',
      permission: 'mes:quality-inspection:read',
      title: '품질 대시보드',
      endpoint: '/mes/quality-inspections',
    },
    {
      path: 'mes/quality/criteria',
      name: 'mes.quality.criteria',
      menuCode: 'mes.quality.criteria',
      permission: 'mes:quality-checklist:read',
      title: '검사 기준 관리',
      endpoint: '/mes/quality-checklists',
    },
    {
      path: 'mes/quality/checklists',
      name: 'mes.quality.checklists',
      menuCode: 'mes.quality.checklists',
      permission: 'mes:quality-checklist:read',
      title: '검사 체크리스트 관리',
      endpoint: '/mes/quality-checklists',
    },
    {
      path: 'mes/quality/requests',
      name: 'mes.quality.requests',
      menuCode: 'mes.quality.requests',
      permission: 'mes:quality-inspection:read',
      title: '품질검사 요청 관리',
      endpoint: '/mes/quality-inspections',
    },
    {
      path: 'mes/quality/results',
      name: 'mes.quality.results',
      menuCode: 'mes.quality.results',
      permission: 'mes:quality-inspection:read',
      title: '품질검사 결과 관리',
      endpoint: '/mes/quality-results',
    },
    {
      path: 'mes/quality/incoming',
      name: 'mes.quality.incoming',
      menuCode: 'mes.quality.incoming',
      permission: 'mes:quality-inspection:read',
      title: '수입검사 관리',
      endpoint: '/mes/quality-inspections',
    },
    {
      path: 'mes/quality/in-process',
      name: 'mes.quality.in-process',
      menuCode: 'mes.quality.in-process',
      permission: 'mes:quality-inspection:read',
      title: '공정 검사 관리',
      endpoint: '/mes/quality-inspections',
    },
    {
      path: 'mes/quality/final',
      name: 'mes.quality.final',
      menuCode: 'mes.quality.final',
      permission: 'mes:quality-inspection:read',
      title: '최종 검사 관리',
      endpoint: '/mes/quality-inspections',
    },
    {
      path: 'mes/quality/status',
      name: 'mes.quality.status',
      menuCode: 'mes.quality.status',
      permission: 'mes:quality-inspection:read',
      title: '검사 현황',
      endpoint: '/mes/quality-inspections',
    },
  ];

  const inventoryRoutes: (MesResourceRoute | RouteRecordRaw)[] = [
    {
      path: 'mes/inventory/goods-receipts',
      name: 'mes.inventory.goods-receipts',
      component: GoodsReceiptsPage,
      meta: {
        requiresAuth: true,
        menuCode: 'mes.inventory.goods-receipts',
        permission: 'mes:goods-receipt:read',
      },
    },
    {
      path: 'mes/inventory/goods-issues',
      name: 'mes.inventory.goods-issues',
      component: GoodsIssuesPage,
      meta: {
        requiresAuth: true,
        menuCode: 'mes.inventory.goods-issues',
        permission: 'mes:goods-issue:read',
      },
    },
    {
      path: 'mes/inventory/material-issues',
      name: 'mes.inventory.material-issues',
      component: MaterialIssuesPage,
      meta: {
        requiresAuth: true,
        menuCode: 'mes.inventory.material-issues',
        permission: 'mes:material-issue:read',
      },
    },
    {
      path: 'mes/inventory/transfers',
      name: 'mes.inventory.transfers',
      component: InventoryTransfersPage,
      meta: {
        requiresAuth: true,
        menuCode: 'mes.inventory.transfers',
        permission: 'mes:inventory-transfer:read',
      },
    },
    {
      path: 'mes/inventory/counts',
      name: 'mes.inventory.counts',
      component: InventoryCountsPage,
      meta: {
        requiresAuth: true,
        menuCode: 'mes.inventory.counts',
        permission: 'mes:inventory-count:read',
      },
    },
    // R.MES.INV.2 — 도식화 포함한 dedicated 페이지(차트+그리드). 더 이상 generic
    // MesStatusPage 경유하지 않으므로 isStatusRoute 목록에서도 빼야 한다.
    {
      path: 'mes/inventory/item-stock-status',
      name: 'mes.inventory.item-stock-status',
      component: InventoryStockStatusPage,
      props: { groupBy: 'item' },
      meta: {
        requiresAuth: true,
        menuCode: 'mes.inventory.item-stock-status',
        permission: 'mes:inventory-balance:read',
      },
    },
    {
      path: 'mes/inventory/warehouse-stock-status',
      name: 'mes.inventory.warehouse-stock-status',
      component: InventoryStockStatusPage,
      props: { groupBy: 'warehouse' },
      meta: {
        requiresAuth: true,
        menuCode: 'mes.inventory.warehouse-stock-status',
        permission: 'mes:inventory-balance:read',
      },
    },
  ];

  function toResourceRoute(r: MesResourceRoute): RouteRecordRaw {
    const route = createAxResourceRoute(r, {
      // (L.MES.QUAL.2 §5.4) 품질 대시보드는 전용 페이지 (KPI·차트·미결 요청).
      component:
        r.menuCode === 'mes.quality.dashboard'
          ? QualityDashboardPage
          : isStatusRoute(r.menuCode)
            ? MesStatusPage
            : isOperationalEndpoint(r.endpoint)
              ? MesOperationalResourcePage
              : MesResourcePage,
    });
    const inspectionType =
      r.menuCode === 'mes.quality.incoming'
        ? 'INCOMING'
        : r.menuCode === 'mes.quality.in-process'
          ? 'IN_PROCESS'
          : r.menuCode === 'mes.quality.final'
            ? 'FINAL'
            : undefined;
    // vue-router의 RouteRecordRaw 유니온은 object spread 시 exactOptionalPropertyTypes와
    // 충돌하므로(선택 속성이 undefined로 넓어짐) 병합 결과를 명시적으로 좁혀준다.
    return inspectionType
      ? ({
          ...route,
          props: { ...(typeof route.props === 'object' ? route.props : {}), inspectionType },
        } as RouteRecordRaw)
      : route;
  }

  function isOperationalEndpoint(endpoint: string): boolean {
    return [
      '/mes/production-plans',
      '/mes/work-orders',
      '/mes/production-records',
      '/mes/quality-checklists',
      '/mes/quality-inspections',
      '/mes/quality-results',
    ].includes(endpoint);
  }

  function isStatusRoute(menuCode: string): boolean {
    return [
      'mes.sales.purchase-status',
      'mes.sales.sales-status',
      'mes.production.wo-record-status',
      'mes.production.daily-status',
      'mes.production.record-status',
      'mes.production.material-issue-status',
      'mes.production.lot-tracking',
      // (L.MES.QUAL.2) mes.quality.dashboard 는 전용 QualityDashboardPage 로 이전됨.
      // R.MES.INV.2 — item/warehouse stock 은 dedicated InventoryStockStatusPage 로 이전됨.
    ].includes(menuCode);
  }

  // sHACCP 라우트 (L.MES.HACCP.1 §5.8)
  const haccpRoutes: RouteRecordRaw[] = [
    // 해썹 현황 대시보드
    {
      path: 'mes/haccp/dashboard',
      name: 'haccp.dashboard',
      component: HaccpDashboardPage,
      meta: {
        requiresAuth: true,
        menuCode: 'haccp.dashboard',
        permission: 'mes:haccp-dashboard:read',
      },
    },
    // 선행요건 — 일반위생·공정점검표(일별)
    {
      path: 'mes/haccp/prerequisite/daily-hygiene',
      name: 'haccp.prerequisite.daily-hygiene',
      component: HygieneCheckLogDailyPage,
      props: { formCategory: 'DAILY' },
      meta: {
        requiresAuth: true,
        menuCode: 'haccp.prerequisite.daily-hygiene',
        permission: 'mes:haccp-prereq-log:read',
      },
    },
    // 선행요건 — 육안검사일지
    {
      path: 'mes/haccp/prerequisite/visual-inspection',
      name: 'haccp.prerequisite.visual-inspection',
      component: HygieneCheckLogVisualPage,
      props: { formCategory: 'WEEKLY' },
      meta: {
        requiresAuth: true,
        menuCode: 'haccp.prerequisite.visual-inspection',
        permission: 'mes:haccp-prereq-log:read',
      },
    },
    // 선행요건 — 생산(작업)일지 (생산실적 재활용)
    {
      path: 'mes/haccp/prerequisite/production-log',
      name: 'haccp.prerequisite.production-log',
      component: HaccpProductionLogPage,
      meta: {
        requiresAuth: true,
        menuCode: 'haccp.prerequisite.production-log',
        permission: 'mes:production-record:read',
      },
    },
    // 선행요건 — 원료수불부 (입출고 집계)
    {
      path: 'mes/haccp/prerequisite/material-ledger',
      name: 'haccp.prerequisite.material-ledger',
      component: HaccpMaterialLedgerPage,
      meta: {
        requiresAuth: true,
        menuCode: 'haccp.prerequisite.material-ledger',
        permission: 'mes:inventory:read',
      },
    },
    // 선행요건 — 제품거래기록서 (판매출고 집계)
    {
      path: 'mes/haccp/prerequisite/product-ledger',
      name: 'haccp.prerequisite.product-ledger',
      component: HaccpProductLedgerPage,
      meta: {
        requiresAuth: true,
        menuCode: 'haccp.prerequisite.product-ledger',
        permission: 'mes:inventory:read',
      },
    },
    // 마스터 — (구) 점검표 서식 관리
    {
      path: 'mes/haccp/master/log-forms',
      name: 'haccp.master.log-forms',
      component: HygieneCheckFormPage,
      meta: {
        requiresAuth: true,
        menuCode: 'haccp.master.log-forms',
        permission: 'mes:haccp-form:read',
      },
    },

    // === HACCP 일지 엔진 (L.MES.HACCP.2 §5.10) ===
    // CCP 기준관리
    {
      path: 'mes/haccp/ccp/definitions',
      name: 'haccp.ccp-master',
      component: CcpMasterPage,
      meta: {
        requiresAuth: true,
        menuCode: 'haccp.ccp-master',
        permission: 'mes:haccp-ccp:read',
      },
    },
    // CCP 모니터링
    {
      path: 'mes/haccp/ccp/monitoring',
      name: 'haccp.ccp-monitoring',
      component: CcpMonitoringPage,
      meta: {
        requiresAuth: true,
        menuCode: 'haccp.ccp-monitoring',
        permission: 'mes:process-data:read',
      },
    },
    // 일지 서식 디자이너
    {
      path: 'mes/haccp/master/log-templates',
      name: 'haccp.master.log-templates',
      component: HaccpTemplateDesignerPage,
      meta: {
        requiresAuth: true,
        menuCode: 'haccp.master.log-templates',
        permission: 'mes:haccp-template:read',
      },
    },
    // CCP 일지 — 등록된 CCP별 메뉴(R3.6). 구 경로는 CCP 기준관리로 리다이렉트.
    { path: 'mes/haccp/logs/ccp', redirect: '/mes/haccp/ccp/definitions' },
    {
      path: 'mes/haccp/logs/ccp/:ccpNo',
      name: 'haccp.ccp-log.detail',
      component: HaccpSheetListPage,
      props: (route) => ({
        // CCP 바인딩 서식은 카테고리 무관(냉장·냉동은 선행요건 등) — ccpNo 필터만 적용
        category: 'CCP,PREREQUISITE,EVENT,ETC',
        pageTitle: String(route.params.ccpNo ?? ''),
        ccpNo: String(route.params.ccpNo ?? ''),
      }),
      meta: {
        requiresAuth: true,
        permission: 'mes:haccp-sheet:read',
      },
    },
    // 선행일지(서식 엔진) — 주기별 메뉴 분리(R3.4)
    { path: 'mes/haccp/logs/prerequisite', redirect: '/mes/haccp/logs/prerequisite/daily' },
    ...([
      ['daily', 'DAILY', '일별 선행일지'],
      ['weekly', 'WEEKLY', '주별 선행일지'],
      ['monthly', 'MONTHLY', '월별 선행일지'],
      ['events', 'ON_EVENT', '수시 일지'],
    ] as const).map(([slug, frequency, label]) => ({
      path: `mes/haccp/logs/prerequisite/${slug}`,
      name: `haccp.prerequisite.${slug}`,
      component: HaccpSheetListPage,
      props: { category: 'PREREQUISITE,EVENT,ETC', pageTitle: label, frequency },
      meta: {
        requiresAuth: true,
        menuCode: `haccp.prerequisite.${slug}`,
        permission: 'mes:haccp-sheet:read',
      },
    })),
    // 일지 시트 작성/조회
    {
      path: 'mes/haccp/sheets/:id',
      name: 'haccp.sheet',
      component: HaccpSheetPage,
      meta: {
        requiresAuth: true,
        menuCode: 'haccp.ccp-log',
        permission: 'mes:haccp-sheet:read',
      },
    },
    // 보건증관리 (P2)
    {
      path: 'mes/haccp/health-certs',
      name: 'haccp.health-cert',
      component: HealthCertPage,
      meta: {
        requiresAuth: true,
        menuCode: 'haccp.health-cert',
        permission: 'mes:haccp-health-cert:read',
      },
    },
  ];

  // SPC (통계적 공정관리) — 공정능력지수·X-Bar/R 관리도·개선대책
  const spcRoutes: RouteRecordRaw[] = [
    ['capability', '공정 능력 지수'],
    ['control-charts', 'X-Bar/R 관리도'],
    ['improvements', 'SPC 개선 대책 관리'],
  ].map(([mode, title]) => {
    const permission =
      mode === 'improvements' ? 'mes:spc-improvement:read' : 'mes:spc-analysis:read';
    return {
      path: `mes/spc/${mode}`,
      name: `mes.spc.${mode}`,
      component: SpcManagementPage,
      props: { mode, title, permission },
      meta: { requiresAuth: true, menuCode: `mes.spc.${mode}`, permission },
    };
  });

  const allMesRoutes: RouteRecordRaw[] = [
    ...masterRoutes,
    ...salesRoutes,
    ...productionRoutes,
    ...qualityRoutes,
    ...spcRoutes,
    ...inventoryRoutes,
    ...haccpRoutes,
  ].flatMap((r) => ('component' in r || 'redirect' in r ? [r] : [toResourceRoute(r as MesResourceRoute)]));

  const mesDataStatusRoutes = buildMesDataStatusRoutes(profileId);

  replaceAdminDashboardRoute(
    coreRoutes,
    {
      component: MesDashboardPage,
      meta: {
        requiresAuth: true,
        menuCode: 'dashboard',
        permission: 'mes:dashboard:read',
      },
    },
    '[mes-fe]',
  );
  appendAdminChildRoutes(coreRoutes, [...mesDataStatusRoutes, ...allMesRoutes], '[mes-fe]');
}

export interface CreateMesFeAppOptions {
  /** Tier 3/4 에서 제품명·타이틀 등만 덮어쓸 때 사용 */
  branding?: Partial<BrandingConfig>;
  mode?: 'saas' | 'on-prem';
  /** on-prem 로그인 시 숨길 테넌트 코드의 서버 값 — 미지정 시 env/VITE_AX_DEFAULT_TENANT_CODE → platform */
  defaultLoginTenantCode?: string;
  /**
   * Tier 3/4 전용 라우트. `createAxApp` 의 `routes` 로 전달되어 코어 라우트 배열 뒤에 합쳐진다.
   * 동일 `path`/`name` 이 이미 MES 표준에 있으면 충돌할 수 있으므로 고객 전용 경로·이름을 쓴다.
   */
  extraRoutes?: RouteRecordRaw[];
  /**
   * 데이터현황 4종 공정 화면의 processTypeCode·차트/KPI 메타.
   * Tier 3 EGM(`kimchi`) vs Tier 4 JYBD(`jayeon-bada`) 시드에 맞게 분기한다.
   */
  processDataStatusProfile?: ProcessDataStatusProfileId;
}

/**
 * MES Tier 2 동일 라우트·레이아웃으로 Vue 앱을 생성한다.
 * Tier 3 `@axsolutionsdev/mes-egm-fe` 등은 `branding`·`extraRoutes` 등으로 차별화한다.
 */
export function createMesFeApp(options?: CreateMesFeAppOptions): AxAppContext {
  ensureMesFeRoutesInstalled(options?.processDataStatusProfile ?? 'jayeon-bada');
  const envRaw = import.meta.env.VITE_DEPLOYMENT_MODE;
  const envMode: 'saas' | 'on-prem' | undefined =
    envRaw === 'saas' || envRaw === 'on-prem' ? envRaw : undefined;
  const mode: 'saas' | 'on-prem' = options?.mode ?? envMode ?? 'saas';
  const branding = mergeMesBranding(options?.branding);
  return createAxApp({
    mode,
    branding,
    defaultColorPreset: AX_TIER_DEFAULT_COLOR_PRESET.mes,
    routes: [...mesReportPrintRoutes, ...mesPopRoutes, ...(options?.extraRoutes ?? [])],
    sidebarPinnedMenuGroupCodes: ['mes.pop'],
    sidebarPinnedMenuLauncherLabel: 'POP/모니터링',
    sidebarDockExtra: AgentDockButton,
    // 자체 AX-Agent 패널(MES 내장 Text-to-SQL + AX-Insight) — 외부 임베드 iframe 대체
    adminLayoutOverlay: MesAgentPanel,
    // MES 워크스페이스 토글 — MES / HACCP / SYSTEM 3분할 ('전체' 없음).
    // - 데이터현황(mes.data-status, =CCP 일지)은 MES 영역(스마트해썹 아님).
    // - HACCP 영역은 haccp.* 만. 스마트해썹 미사용 테넌트는 서버가 haccp.* 를 숨겨
    //   'HACCP' 버튼도 사라진다(hasScopeMenus/usableScopeOptions).
    // - rootCodes 는 베이스 카탈로그(system)와 EGM/JYBD 슬림 카탈로그(jybd.*) 양쪽 코드를 포함.
    // storageKey 분리로 ERP 와 localStorage 충돌 없음.
    sidebarMenuScope: {
      storageKey: 'ax-sidebar-menu-scope-mes',
      showAllOption: false,
      options: [
        {
          value: 'mes',
          label: 'MES',
          short: 'MES',
          rootCodes: [
            'dashboard',
            'mes.master',
            'mes.sales',
            'mes.production',
            'mes.quality',
            'mes.spc',
            'mes.inventory',
            'mes.data-status',
            'mes.pop',
          ],
        },
        {
          // rootCodes 에 'dashboard'(항상 존재) 를 넣지 않는다 — 넣으면 스마트해썹 OFF 로
          // haccp.* 가 모두 숨겨져도 HACCP 옵션이 'usable' 로 남아 버튼이 사라지지 않는다.
          // haccp.* 루트만 두어, 스마트해썹 OFF 테넌트는 HACCP 버튼 자체가 제거되도록 한다.
          value: 'haccp',
          label: 'HACCP',
          short: 'HACCP',
          rootCodes: [
            'haccp.dashboard',
            'haccp.ccp-master',
            'haccp.ccp-log',
            'haccp.ccp-monitoring',
            'haccp.master',
            'haccp.prerequisite',
            'haccp.health-cert',
          ],
        },
        {
          value: 'system',
          label: 'SYSTEM',
          short: 'SYS',
          rootCodes: ['dashboard', 'system', 'jybd.users-root', 'jybd.system-root'],
        },
      ],
      adminRoles: ['ADMIN', 'SUPER_ADMIN'],
    },
    ...(options?.defaultLoginTenantCode !== undefined
      ? { defaultLoginTenantCode: options.defaultLoginTenantCode }
      : {}),
  });
}

// =============================================================================
// (L.MES.SK.4b) Tier 3/4 확장 지점 — 제네릭 리소스 페이지 + 레지스트리
//
// 고객사 FE 는 아래를 이용해 자기 전용 endpoint 의 CRUD/조회 화면을
// mes-ax 표준 제네릭 페이지로 구성할 수 있다 (core-fe `appendAdminChildRoutes`
// 와 조합, mes-egm HOW-TO-ADD-A-SCREEN 참조).
// =============================================================================
export { default as MesGridSplitView } from './components/MesGridSplitView.vue';
export {
  registerMesResourceConfigs,
  registerMesReferenceRequests,
  type MesReferenceRequest,
  type MesResourceAction,
  type MesResourceConfig,
  type MesResourceFieldConfig,
} from './views/mes-resource-registry.js';
export {
  registerQualityInspectorOptions,
  type QualityInspectorOption,
} from './views/quality/quality-inspector-registry.js';

/** 문서형 CRUD 제네릭 페이지 (레지스트리 config 필요) — lazy component */
export const MesOperationalResourcePageAsync: AsyncPage = MesOperationalResourcePage;
/** 마스터형 제네릭 페이지 — lazy component */
export const MesResourcePageAsync: AsyncPage = MesResourcePage;
/** 조회(현황)형 제네릭 페이지 — lazy component */
export const MesStatusPageAsync: AsyncPage = MesStatusPage;
